import React from "react";
import { Mail, MapPin, MessageCircle, Clock, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { COMPANY } from "@/lib/site";

const channelClasses =
  "block rounded-[16px] border border-slate-200 bg-white p-4 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]";

const Channel = ({ href, testId, icon: Icon, iconWrap, label, value, hint, external }) => (
  <a
    href={href}
    aria-label={`${label}: ${value}`}
    data-testid={testId}
    className={channelClasses}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
  >
    <div className="flex items-start gap-4">
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <p className="text-base font-semibold text-[#0b1d3a]">{value}</p>
        <p className="mt-1 text-sm text-slate-600">{hint}</p>
      </div>
    </div>
  </a>
);

const AddressBlock = () => (
  <Card
    data-testid="contact-address-block"
    className="rounded-[16px] border-slate-200 bg-[#f7fafc] p-4 shadow-none"
  >
    <div className="flex items-start gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fef3e8]">
        <MapPin className="h-5 w-5 text-[#b4611b]" aria-hidden="true" />
      </span>
      <div>
        <p
          data-testid="contact-legal-name"
          className="text-base font-semibold text-[#0b1d3a]"
        >
          {COMPANY.name}
        </p>
        <p className="mt-1 text-sm font-medium text-[#0b1d3a]">{COMPANY.address.line1}</p>
        <p className="text-sm font-medium text-[#0b1d3a]">
          {COMPANY.address.city} - {COMPANY.address.country}
        </p>
        <a
          href={COMPANY.mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-maps-link"
          className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f6e73] transition-colors hover:text-[#0b1d3a]"
        >
          View on Google Maps
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>

        <div className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-3 text-sm text-slate-600">
          <Clock className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          {COMPANY.hours}
        </div>
      </div>
    </div>
  </Card>
);

export const ContactInfo = () => (
  <div className="space-y-3">
    <AddressBlock />
    <Channel
      href={COMPANY.emailLink}
      testId="contact-email-link"
      icon={Mail}
      iconWrap="bg-[#eef2f8] text-[#123055]"
      label="Email"
      value={COMPANY.email}
      hint="For written enquiries and documents"
    />
    <Channel
      href={COMPANY.whatsappLink}
      testId="contact-whatsapp-link"
      icon={MessageCircle}
      iconWrap="bg-[#eef7f7] text-[#0f6e73]"
      label="WhatsApp"
      value={COMPANY.whatsappDisplay}
      hint="Tap to message the head office"
      external
    />
  </div>
);
